"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DetailedStatistics = void 0;
const ui_1 = require("@app/component/ui");
const data_grid_1 = require("@app/component/ui/data-grid");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const format_hours_1 = require("@app/util/format-hours");
const maybe_1 = require("@app/util/functional/maybe");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const shift_context_1 = require("../shift-context");
const use_detailed_statistics_1 = require("../util/use-detailed-statistics");
const pauseHasDuration = (t) => (0, util_1.isValue)(t.duration);
const statsHasWorkTime = (t) => (0, util_1.isValue)(t.workMinutes);
const statsHasOverTime = (t) => (0, util_1.isValue)(t.overTimeTotalMinutes);
const statsHasSaturdayMinutes = (t) => (0, util_1.isValue)(t.saturdayMinutes);
const statsHasSundayMinutes = (t) => (0, util_1.isValue)(t.sundayMinutes);
const statsHasNightMinutes = (t) => (0, util_1.isValue)(t.nightMinutes);
const statsHasHolidayMinutes = (t) => (0, util_1.isValue)(t.holidayMinutes);
// const statsHasStandByActivityMinutes = (
//   t: ShiftStats
// ): t is ShiftStats & { standByActivityMinutes: number } =>
//   isValue(t.standByActivityMinutes)
const DetailedStatistics = ({ shift, }) => {
    const { initialData, formControl } = (0, shift_context_1.useShift)();
    const _shift = new maybe_1.Maybe(shift);
    const _stats = _shift.map((sh) => sh.stats).map((st) => st.filter(util_1.isValue));
    const employees = (0, store_1.useRootSelector)((state) => state.employees);
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const userId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'user' });
    const contractId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'contractId' });
    const currentEmployee = react_1.default.useMemo(() => ((0, util_1.isValue)(userId) ? employees[userId] : undefined), [userId]);
    const currentContract = react_1.default.useMemo(() => currentEmployee?.contracts.find((con) => con.id === contractId), [contractId]);
    const currentContractType = react_1.default.useMemo(() => workspace?.contractTypes?.find((con) => con.id === currentContract?.type)
        ?.name, [contractId]);
    const _detailedStatistics = (0, use_detailed_statistics_1.useDetailedStatistics)({
        shouldLoadDetailedStatistics: initialData?.type === 'live',
        shiftId: initialData?.shiftId,
        fallback: shift,
    });
    const pauseMinutes = _shift
        .map((sh) => sh.pauses)
        .map((_pauses) => _pauses.filter(util_1.isValue))
        .map((_pauses) => _pauses.filter(pauseHasDuration))
        .map((_pauses) => _pauses.reduce((acc, pause) => acc + pause.duration, 0))
        .get(() => 0);
    const workTimeMinutes = _stats
        .map((st) => st.filter(statsHasWorkTime))
        .map((st) => st.reduce((acc, stat) => acc + stat.workMinutes, 0))
        .get(() => 0);
    const plannedMinutes = _shift
        .map((sh) => sh.period)
        .map((_period) => (0, util_1.isValue)(_period.start) && (0, util_1.isValue)(_period.end)
        ? { start: (0, moment_1.default)(_period.start), end: (0, moment_1.default)(_period.end) }
        : undefined)
        .map((_period) => _period.end.startOf('hour').diff(_period.start.startOf('hour'), 'minutes') -
        pauseMinutes)
        .get(() => 0);
    const overTimeMinutes = _stats
        .map((st) => st.filter(statsHasOverTime))
        .map((st) => st.reduce((acc, stat) => acc + stat.overTimeTotalMinutes, 0))
        .map((minutes) => (minutes === 0 ? undefined : minutes))
        .map((minutes) => (0, format_hours_1.formatHours)(minutes / 60))
        .get(() => undefined);
    const differenceMinutes = plannedMinutes - workTimeMinutes;
    const differenceSymbol = differenceMinutes > 0 ? '+' : '-';
    const workTimeIncludingPause = workTimeMinutes + pauseMinutes;
    const saturdayMinutes = _stats
        .map((st) => st.filter(statsHasSaturdayMinutes))
        .map((st) => st.reduce((acc, stat) => acc + stat.saturdayMinutes, 0))
        .map((minutes) => (minutes === 0 ? undefined : minutes))
        .map((minutes) => (0, format_hours_1.formatHours)(minutes / 60))
        .get(() => undefined);
    const sundayMinutes = _stats
        .map((st) => st.filter(statsHasSundayMinutes))
        .map((st) => st.reduce((acc, stat) => acc + stat.sundayMinutes, 0))
        .map((minutes) => (minutes === 0 ? undefined : minutes))
        .map((minutes) => (0, format_hours_1.formatHours)(minutes / 60))
        .get(() => undefined);
    const nightMinutes = _stats
        .map((st) => st.filter(statsHasNightMinutes))
        .map((st) => st.reduce((acc, stat) => acc + stat.nightMinutes, 0))
        .map((minutes) => (minutes === 0 ? undefined : minutes))
        .map((minutes) => (0, format_hours_1.formatHours)(minutes / 60))
        .get(() => undefined);
    const holidayMinutes = _stats
        .map((st) => st.filter(statsHasHolidayMinutes))
        .map((st) => st.reduce((acc, stat) => acc + stat.holidayMinutes, 0))
        .map((minutes) => (minutes === 0 ? undefined : minutes))
        .map((minutes) => (0, format_hours_1.formatHours)(minutes / 60))
        .get(() => undefined);
    // const standByActivityMinutes = _stats
    //   .map((st) => st.filter(statsHasStandByActivityMinutes))
    //   .map((st) => st.reduce((acc, stat) => acc + stat.standByActivityMinutes, 0))
    //   .map((minutes) => (minutes === 0 ? undefined : minutes))
    //   .map((minutes) => formatHours(minutes / 60))
    //   .get(() => undefined)
    const periodString = new maybe_1.Maybe(shift?.period)
        .map((_period) => (0, util_1.isValue)(_period.start) && (0, util_1.isValue)(_period.end)
        ? { start: _period.start, end: _period.end }
        : undefined)
        .map(({ start, end }) => `${(0, moment_1.default)(start).format('HH:mm')} - ${(0, moment_1.default)(end).format('HH:mm')}`)
        .get(() => undefined);
    const titleInfo = [
        shift?.userName,
        (0, util_1.isValue)(shift?.period?.start)
            ? (0, moment_1.default)(shift?.period?.start).format('DD.MM.YYYY')
            : undefined,
        periodString,
    ].filter(util_1.isValue);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Text, { type: 'body-large', weight: 'bold' },
            (0, util_1.t)('DETAILED_STATISTIC'),
            titleInfo.length > 0 ? ` - ${titleInfo.join(' ')}` : null),
        react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
        react_1.default.createElement(data_grid_1.DataGrid, { columns: [
                {
                    cells: [
                        {
                            label: (0, util_1.t)('PLAN_HOURS'),
                            value: plannedMinutes === 0
                                ? undefined
                                : (0, format_hours_1.formatHours)(plannedMinutes / 60),
                        },
                        {
                            label: (0, util_1.t)('ATTENDANCE_HOURS'),
                            value: workTimeMinutes === 0
                                ? undefined
                                : (0, format_hours_1.formatHours)(workTimeMinutes / 60),
                        },
                        {
                            label: (0, util_1.t)('OVERTIME'),
                            value: overTimeMinutes,
                        },
                        {
                            label: (0, util_1.t)('DIFFERENCE'),
                            value: differenceMinutes === 0
                                ? undefined
                                : `${differenceSymbol}${(0, format_hours_1.formatHours)(Math.abs(differenceMinutes / 60))}`,
                        },
                        {
                            label: (0, util_1.t)('CYC_PAUSES'),
                            value: pauseMinutes === 0 ? undefined : (0, format_hours_1.formatHours)(pauseMinutes / 60),
                        },
                        {
                            label: (0, util_1.t)('WORK_TIME_WITH_PAUSES'),
                            value: workTimeIncludingPause === 0
                                ? undefined
                                : (0, format_hours_1.formatHours)(workTimeIncludingPause / 60),
                        },
                        {
                            label: (0, util_1.t)('AGREEMENT'),
                            value: currentContractType,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_TRANSFERS'),
                            value: (0, util_1.isDefined)(shift?.transfer)
                                ? (0, format_hours_1.formatHours)(workTimeMinutes / 60)
                                : undefined,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_WORKDAYTIME'),
                            value: (0, util_1.isValue)(_detailedStatistics) &&
                                (0, util_1.isValue)(_detailedStatistics.workDayMinutes)
                                ? (0, format_hours_1.formatHours)(_detailedStatistics.workDayMinutes / 60)
                                : undefined,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_HOURS_SATURDAY'),
                            value: saturdayMinutes,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_HOURS_SUNDAY'),
                            value: sundayMinutes,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_NIGHT_HOURS'),
                            value: nightMinutes,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_HOURS_HOLIDAY'),
                            value: holidayMinutes,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_STANDBY_ACTIVITY_HOURS'),
                            value: (0, util_1.isValue)(_detailedStatistics) &&
                                (0, util_1.isValue)(_detailedStatistics.standByActivityMinutes) &&
                                _detailedStatistics.standByActivityMinutes !== 0
                                ? (0, format_hours_1.formatHours)(_detailedStatistics.standByActivityMinutes / 60)
                                : undefined,
                        },
                        {
                            label: (0, util_1.t)('REPORTS_EMP_VAL_STANDBY_HOURS'),
                            value: (0, util_1.isValue)(_detailedStatistics) &&
                                (0, util_1.isValue)(_detailedStatistics.standByMinutes) &&
                                _detailedStatistics.standByMinutes !== 0
                                ? (0, format_hours_1.formatHours)(_detailedStatistics.standByMinutes / 60)
                                : undefined,
                        },
                        {
                            label: `${(0, util_1.t)('OVERTIME')}: ${(0, util_1.t)('REPORTS_OVERTIME_AGREED_UNPAID_MINUTES')}`,
                            value: (0, util_1.isValue)(_detailedStatistics) &&
                                (0, util_1.isValue)(_detailedStatistics.overTimeUnpaidMinutes) &&
                                (0, util_1.isValue)(_detailedStatistics.overTimeAgreedMinutes) &&
                                _detailedStatistics.overTimeUnpaidMinutes > 0 &&
                                _detailedStatistics.overTimeAgreedMinutes > 0
                                ? (0, format_hours_1.formatHours)(_detailedStatistics.overTimeAgreedMinutes / 60)
                                : undefined,
                        },
                        {
                            label: `${(0, util_1.t)('OVERTIME')}: ${(0, util_1.t)('REPORTS_OVERTIME_ORDERED_UNPAID_MINUTES')}`,
                            value: (0, util_1.isValue)(_detailedStatistics) &&
                                (0, util_1.isValue)(_detailedStatistics.overTimeUnpaidMinutes) &&
                                (0, util_1.isValue)(_detailedStatistics.overTimeOrderedMinutes) &&
                                _detailedStatistics.overTimeUnpaidMinutes > 0 &&
                                _detailedStatistics.overTimeOrderedMinutes > 0
                                ? (0, format_hours_1.formatHours)(_detailedStatistics.overTimeOrderedMinutes / 60)
                                : undefined,
                        },
                    ],
                },
            ] })));
};
exports.DetailedStatistics = DetailedStatistics;
